/**
 * UserStore - Singleton class to manage user data globally
 * Provides a single point of access for user information throughout the application
 */
class UserStore {
    constructor() {
        if (UserStore.instance) {
            return UserStore.instance;
        }

        this.user = null;
        this.listeners = new Set();
        UserStore.instance = this;
    }

    /**
     * Get the singleton instance
     * @returns {UserStore} The singleton instance
     */
    static getInstance() {
        if (!UserStore.instance) {
            UserStore.instance = new UserStore();
        }
        return UserStore.instance;
    }

    /**
     * Set the current user data
     * @param {Object} userData - The user object from API response
     */
    setUser(userData) {
        this.user = userData;
        this.notifyListeners();

        // Also store in localStorage for persistence
        if (userData) {
            localStorage.setItem("currentUser", JSON.stringify(userData));
        } else {
            localStorage.removeItem("currentUser");
        }
    }

    /**
     * Get the current user data
     * @returns {Object|null} The current user object or null
     */
    getUser() {
        // If no user in memory, try to load from localStorage
        if (!this.user) {
            const storedUser = localStorage.getItem("currentUser");
            if (storedUser) {
                try {
                    this.user = JSON.parse(storedUser);
                } catch (error) {
                    console.error("Error parsing stored user data:", error);
                    localStorage.removeItem("currentUser");
                }
            }
        }
        return this.user;
    }

    /**
     * Get specific user property
     * @param {string} property - The property name to get
     * @returns {any} The property value or undefined
     */
    getUserProperty(property) {
        const user = this.getUser();
        return user ? user[property] : undefined;
    }

    /**
     * Check if user is logged in
     * @returns {boolean} True if user is logged in and active
     */
    isLoggedIn() {
        const user = this.getUser();
        return user && user.active === true;
    }

    /**
     * Check if user has specific role
     * @param {string} role - The role to check
     * @returns {boolean} True if user has the specified role
     */
    hasRole(role) {
        const user = this.getUser();
        return user && user.role === role;
    }

    /**
     * Check if user is admin
     * @returns {boolean} True if user is admin
     */
    isAdmin() {
        return this.hasRole("admin");
    }

    /**
     * Check if user is demo user
     * @returns {boolean} True if user is demo user
     */
    isDemoUser() {
        const user = this.getUser();
        return user && user.role === "demo";
    }

    /**
     * Clear user data (logout)
     */
    clearUser() {
        this.user = null;
        localStorage.removeItem("currentUser");
        this.notifyListeners();
    }

    /**
     * Add a listener for user changes
     * @param {Function} callback - Function to call when user changes
     */
    addListener(callback) {
        this.listeners.add(callback);
    }

    /**
     * Remove a listener
     * @param {Function} callback - The callback to remove
     */
    removeListener(callback) {
        this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of user changes
     */
    notifyListeners() {
        this.listeners.forEach((callback) => {
            try {
                callback(this.user);
            } catch (error) {
                console.error("Error in user store listener:", error);
            }
        });
    }

    /**
     * Update specific user properties
     * @param {Object} updates - Object with properties to update
     */
    updateUser(updates) {
        if (this.user) {
            this.user = { ...this.user, ...updates };
            this.setUser(this.user);
        }
    }

    /**
     * Get user display name (email or name)
     * @returns {string} User display name
     */
    getDisplayName() {
        const user = this.getUser();
        return user ? user.name || user.email || "Unknown User" : "Guest";
    }
}

// Export singleton instance
const userStore = UserStore.getInstance();
export default userStore;

// Also export the class for type checking if needed
export { UserStore };
